import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
  Tooltip,
  Stack,
  Checkbox,
  ListItemText,
  OutlinedInput,
  InputAdornment,
  LinearProgress,
  Tabs,
  Tab,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import PhoneCallbackRoundedIcon from "@mui/icons-material/PhoneCallbackRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import HowToRegRoundedIcon from "@mui/icons-material/HowToRegRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import StoreRoundedIcon from "@mui/icons-material/StoreRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";

import { crmAPI, adminAPI } from "../services/api";
import { EmptyState } from "../components/ui";

const DATE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "this_week", label: "This Week" },
  { id: "last_week", label: "Last Week" },
  { id: "this_month", label: "This Month" },
  { id: "last_month", label: "Last Month" },
  { id: "last_3_months", label: "Last 3 Months" },
  { id: "last_6_months", label: "Last 6 Months" },
  { id: "fy_current", label: "Current FY (2026-27)" },
  { id: "fy_previous", label: "Last FY (2025-26)" },
  { id: "custom", label: "Custom Range" },
];

const compactMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 280,
      "& .MuiMenuItem-root": {
        py: 0.5,
        minHeight: 30,
        fontSize: "12.5px",
      },
      "& .MuiCheckbox-root": {
        py: 0.25,
        px: 0.5,
      },
      "& .MuiListItemText-primary": {
        fontSize: "12.5px",
      },
    },
  },
};

const getDateRangeForPreset = (preset) => {
  const now = new Date();
  const format = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  switch (preset) {
    case "today":
      return { from: format(now), to: format(now) };
    case "yesterday": {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { from: format(y), to: format(y) };
    }
    case "this_week": {
      const d = new Date(now);
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1);
      d.setDate(diff);
      return { from: format(d), to: format(now) };
    }
    case "last_week": {
      const d = new Date(now);
      const day = d.getDay();
      const diff = d.getDate() - day - 6 + (day === 0 ? -6 : 1);
      const start = new Date(d.setDate(diff));
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { from: format(start), to: format(end) };
    }
    case "this_month": {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: format(start), to: format(now) };
    }
    case "last_month": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: format(start), to: format(end) };
    }
    case "last_3_months": {
      const start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      return { from: format(start), to: format(now) };
    }
    case "last_6_months": {
      const start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      return { from: format(start), to: format(now) };
    }
    case "fy_current": {
      const fyYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      const start = new Date(fyYear, 3, 1);
      return { from: format(start), to: format(now) };
    }
    case "fy_previous": {
      const prevYear = now.getMonth() >= 3 ? now.getFullYear() - 1 : now.getFullYear() - 2;
      const start = new Date(prevYear, 3, 1);
      const end = new Date(prevYear + 1, 2, 31);
      return { from: format(start), to: format(end) };
    }
    default:
      return null;
  }
};

const getStatusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s.includes("confirm") || s.includes("convert") || s.includes("paid")) {
    return { bg: "#dcfce7", color: "#166534", border: "#22c55e" };
  }
  if (s.includes("follow_up") || s.includes("callback") || s.includes("call_back")) {
    return { bg: "#fff7ed", color: "#c2410c", border: "#f97316" };
  }
  if (s.includes("not_interested") || s.includes("cancel") || s.includes("lost")) {
    return { bg: "#fee2e2", color: "#b91c1c", border: "#ef4444" };
  }
  if (s.includes("no_response")) {
    return { bg: "#f1f5f9", color: "#475569", border: "#94a3b8" };
  }
  return { bg: "#f3e8ff", color: "#6b21a8", border: "#a855f7" };
};

const LeadReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview"); // 'overview' | 'user_performance' | 'status_breakdown' | 'activity'
  const [datePreset, setDatePreset] = useState("this_month");
  const [fromDate, setFromDate] = useState(() => {
    const range = getDateRangeForPreset("this_month");
    return range ? range.from : "";
  });
  const [toDate, setToDate] = useState(() => {
    const range = getDateRangeForPreset("this_month");
    return range ? range.to : "";
  });

  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState([]);
  const [selectedEvents, setSelectedEvents] = useState([]); // Array of event/trek names
  const [selectedAdminUsers, setSelectedAdminUsers] = useState([]); // Array of admin user IDs
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [searchTableEvent, setSearchTableEvent] = useState("");
  const [chartViewMode, setChartViewMode] = useState("day"); // 'day' | 'week'

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [sortField, setSortField] = useState("total");
  const [sortOrder, setSortOrder] = useState("desc");

  // User performance table state
  const [searchTableUser, setSearchTableUser] = useState("");
  const [userPage, setUserPage] = useState(0);
  const [userRowsPerPage, setUserRowsPerPage] = useState(15);
  const [userSortField, setUserSortField] = useState("total");
  const [userSortOrder, setUserSortOrder] = useState("desc");

  const navigateToLeadRequests = (overrides = {}) => {
    const params = new URLSearchParams();
    params.set("page", "1");
    if (datePreset) params.set("datePreset", datePreset);
    if (fromDate) params.set("fromDate", fromDate);
    if (toDate) params.set("toDate", toDate);
    if (sourceFilter && sourceFilter !== "all") params.set("source", sourceFilter);
    if (selectedEvents && selectedEvents.length > 0) params.set("events", selectedEvents.join(","));
    if (selectedAdminUsers && selectedAdminUsers.length > 0) params.set("adminUsers", selectedAdminUsers.join(","));
    if (statusFilter && statusFilter.length > 0) params.set("statuses", statusFilter.join(","));

    Object.entries(overrides).forEach(([key, val]) => {
      if (val === undefined || val === null || val === "") {
        params.delete(key);
      } else {
        params.set(key, val);
      }
    });

    navigate(`/lead-requests?${params.toString()}`);
  };

  // Fetch filter options (all treks for event multi-select)
  const { data: filterOptionsData } = useQuery(
    ["admin-report-filter-options"],
    async () => {
      const res = await adminAPI.getReportFilterOptions();
      return res.data?.data || {};
    },
    { staleTime: 10 * 60 * 1000 }
  );
  const treks = useMemo(() => filterOptionsData?.treks || [], [filterOptionsData]);

  // Fetch admin users / staff members for multi-select
  const { data: assigneesData } = useQuery(
    ["crm-assignees"],
    async () => {
      const res = await crmAPI.getAssignees();
      return res.data?.data || [];
    },
    { staleTime: 10 * 60 * 1000 }
  );
  const assignees = useMemo(() => assigneesData || [], [assigneesData]);

  // Handle Preset Change
  const handlePresetChange = (newPreset) => {
    setDatePreset(newPreset);
    if (newPreset !== "custom") {
      const range = getDateRangeForPreset(newPreset);
      if (range) {
        setFromDate(range.from);
        setToDate(range.to);
      }
    }
    // Auto switch chart mode
    if (newPreset === "last_3_months" || newPreset === "last_6_months" || newPreset === "fy_current" || newPreset === "fy_previous") {
      setChartViewMode("week");
    } else {
      setChartViewMode("day");
    }
    setPage(0);
  };

  // Main Lead Reports Query
  const { data, isLoading, isFetching, refetch } = useQuery(
    [
      "lead-reports",
      fromDate,
      toDate,
      statusFilter,
      sourceFilter,
      selectedEvents,
      selectedAdminUsers,
    ],
    () =>
      crmAPI.getReports({
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        statuses: statusFilter.length > 0 ? statusFilter.join(",") : undefined,
        source: sourceFilter !== "all" ? sourceFilter : undefined,
        events: selectedEvents.length > 0 ? selectedEvents.join(",") : undefined,
        adminUsers: selectedAdminUsers.length > 0 ? selectedAdminUsers.join(",") : undefined,
      }),
    { keepPreviousData: true }
  );

  const report = data?.data?.data || {};
  const byEvent = report.byEvent || [];
  const byUser = report.byUser || [];
  const byStatus = report.byStatus || [];
  const byCategory = report.byCategory || [];
  const chartData = report.chartData || [];
  const recentChanges = report.recentStatusChanges || [];

  // Update status dropdown options
  useEffect(() => {
    if (byStatus && byStatus.length > 0) {
      setAvailableStatuses((prev) => {
        const map = new Map(prev.map((p) => [p.status, p]));
        byStatus.forEach((s) => {
          if (!map.has(s.status)) {
            map.set(s.status, { status: s.status, label: s.label || s.status });
          }
        });
        return Array.from(map.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        );
      });
    }
  }, [byStatus]);

  // Combined unique list of events for the event Autocomplete
  const allAvailableEventNames = useMemo(() => {
    const set = new Set();
    treks.forEach((t) => t.name && set.add(t.name));
    byEvent.forEach((e) => e.event && set.add(e.event));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [treks, byEvent]);

  // Summary Metrics
  const summary = useMemo(() => {
    if (report.summary) return report.summary;
    let totalLeads = 0;
    let convertedCount = 0;
    let inFollowUpCount = 0;
    let lostCount = 0;
    byEvent.forEach((e) => {
      totalLeads += Number(e.total || 0);
      convertedCount += Number(e.converted || 0);
      inFollowUpCount += Number(e.inFollowUp || 0);
      lostCount += Number(e.lost || 0);
    });
    let uncontactedCount = 0;
    byStatus.forEach((s) => {
      if (s.status === "(not set)" || !s.status) {
        uncontactedCount += Number(s.count || 0);
      }
    });
    if (totalLeads > 0 && uncontactedCount > totalLeads) {
      uncontactedCount = Math.max(0, totalLeads - convertedCount - inFollowUpCount - lostCount);
    }
    return {
      totalLeads,
      convertedCount,
      conversionRate: totalLeads > 0 ? Math.round((convertedCount / totalLeads) * 1000) / 10 : 0,
      inFollowUpCount,
      inFollowUpRate: totalLeads > 0 ? Math.round((inFollowUpCount / totalLeads) * 1000) / 10 : 0,
      uncontactedCount,
      uncontactedRate: totalLeads > 0 ? Math.round((uncontactedCount / totalLeads) * 1000) / 10 : 0,
      lostCount,
      lostRate: totalLeads > 0 ? Math.round((lostCount / totalLeads) * 1000) / 10 : 0,
    };
  }, [report.summary, byEvent, byStatus]);

  // Processed Chart Data (Day vs Week grouping)
  const processedChartData = useMemo(() => {
    if (chartViewMode === "day" || !chartData || chartData.length === 0) {
      return chartData;
    }
    const weekMap = new Map();
    chartData.forEach((d) => {
      if (!d.date) return;
      const parts = d.date.split("-");
      const dt = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      const day = dt.getDay();
      const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
      const weekStart = new Date(dt.setDate(diff));
      const pad = (n) => String(n).padStart(2, "0");
      const weekKey = `${pad(weekStart.getMonth() + 1)}/${pad(weekStart.getDate())}`;
      const cur = weekMap.get(weekKey) || {
        date: `Wk ${weekKey}`,
        label: `Week starting ${weekKey}`,
        leads: 0,
        converted: 0,
      };
      cur.leads += Number(d.leads || 0);
      cur.converted += Number(d.converted || 0);
      weekMap.set(weekKey, cur);
    });
    return Array.from(weekMap.values());
  }, [chartData, chartViewMode]);

  // Chart Max Value for Scaling & Y-Axis Grid
  const maxChartVal = useMemo(() => {
    let max = 10;
    processedChartData.forEach((d) => {
      if (Number(d.leads || 0) > max) max = Number(d.leads);
    });
    // Round up to clean multiple
    if (max <= 20) return 20;
    if (max <= 50) return 50;
    if (max <= 100) return 100;
    const factor = Math.pow(10, Math.floor(Math.log10(max)));
    return Math.ceil(max / factor) * factor;
  }, [processedChartData]);

  // Filtered and Sorted Event Rows in Table
  const filteredEvents = useMemo(() => {
    let list = byEvent;
    if (searchTableEvent.trim()) {
      const q = searchTableEvent.toLowerCase();
      list = list.filter((r) => String(r.event || "").toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];
      if (typeof valA === "string") {
        return sortOrder === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      }
      valA = Number(valA || 0);
      valB = Number(valB || 0);
      return sortOrder === "asc" ? valA - valB : valB - valA;
    });
  }, [byEvent, searchTableEvent, sortField, sortOrder]);

  const paginatedEvents = useMemo(() => {
    return filteredEvents.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredEvents, page, rowsPerPage]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const handleUserSort = (field) => {
    if (userSortField === field) {
      setUserSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setUserSortField(field);
      setUserSortOrder("desc");
    }
  };

  const filteredUsers = useMemo(() => {
    let list = byUser;
    if (searchTableUser.trim()) {
      const q = searchTableUser.toLowerCase().trim();
      list = list.filter(
        (u) =>
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q) ||
          (u.role || "").toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      let valA = a[userSortField];
      let valB = b[userSortField];
      if (typeof valA === "string") {
        return userSortOrder === "asc"
          ? String(valA).localeCompare(String(valB))
          : String(valB).localeCompare(String(valA));
      }
      valA = Number(valA || 0);
      valB = Number(valB || 0);
      return userSortOrder === "asc" ? valA - valB : valB - valA;
    });
  }, [byUser, searchTableUser, userSortField, userSortOrder]);

  const paginatedUsers = useMemo(() => {
    return filteredUsers.slice(
      userPage * userRowsPerPage,
      userPage * userRowsPerPage + userRowsPerPage
    );
  }, [filteredUsers, userPage, userRowsPerPage]);

  const handleClearFilters = () => {
    setDatePreset("this_month");
    const range = getDateRangeForPreset("this_month");
    setFromDate(range ? range.from : "");
    setToDate(range ? range.to : "");
    setSourceFilter("all");
    setStatusFilter([]);
    setSelectedEvents([]);
    setSelectedAdminUsers([]);
    setSearchTableEvent("");
    setSearchTableUser("");
    setChartViewMode("week");
    setPage(0);
    setUserPage(0);
  };

  const isFiltered =
    datePreset !== "this_month" ||
    sourceFilter !== "all" ||
    statusFilter.length > 0 ||
    selectedEvents.length > 0 ||
    selectedAdminUsers.length > 0 ||
    searchTableEvent.trim() !== "";

  // CSV Export Function
  const exportToCSV = () => {
    if (activeTab === "user_performance") {
      if (!filteredUsers || filteredUsers.length === 0) return;
      const headers = [
        "User ID",
        "User / Counselor Name",
        "Email",
        "Role",
        "Total Leads",
        "Converted / Confirmed",
        "Active Pipeline",
        "New / Not Set",
        "Lost / Dropped",
        "Conversion Rate (%)",
      ];
      const rows = filteredUsers.map((u) => [
        u.userId,
        `"${(u.name || "").replace(/"/g, '""')}"`,
        `"${(u.email || "").replace(/"/g, '""')}"`,
        `"${(u.role || "").replace(/"/g, '""')}"`,
        u.total || 0,
        u.converted || 0,
        u.active || 0,
        u.newOrNotSet || 0,
        u.lost || 0,
        `${u.conversionRate || 0}%`,
      ]);
      const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invincible_counselor_reports_${fromDate}_to_${toDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    if (!filteredEvents || filteredEvents.length === 0) return;
    const headers = [
      "Event Name",
      "Total Leads",
      "Converted / Confirmed",
      "Active Follow-Up",
      "Lost / Dropped",
      "Conversion Rate (%)",
    ];
    const rows = filteredEvents.map((r) => [
      `"${(r.event || "").replace(/"/g, '""')}"`,
      r.total || 0,
      r.converted || 0,
      r.inFollowUp || 0,
      r.lost || 0,
      `${r.conversionRate || 0}%`,
    ]);
    const csvContent = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `invincible_lead_reports_${fromDate}_to_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="module-page" style={{ padding: "20px 24px" }}>
      {/* Top Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 1 }}>
            <AssessmentRoundedIcon color="primary" /> Lead Reports
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
            Performance overview of leads, conversions, and pipeline status by event and counselor.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, borderColor: "#cbd5e1" }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            startIcon={<FileDownloadRoundedIcon />}
            onClick={exportToCSV}
            disabled={activeTab === "user_performance" ? filteredUsers.length === 0 : filteredEvents.length === 0}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5, boxShadow: "none" }}
          >
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {/* Primary Navigation Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, val) => setActiveTab(val)}
        sx={{
          mb: 2.5,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "14px", minHeight: 44 },
        }}
      >
        <Tab
          value="overview"
          label="Overview & Events"
          icon={<BarChartRoundedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
        />
        <Tab
          value="user_performance"
          label={`Counselors / Users (${byUser.length})`}
          icon={<BadgeRoundedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
        />
        <Tab
          value="status_breakdown"
          label={`Status Pipeline (${byStatus.length})`}
          icon={<FilterAltRoundedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
        />
        <Tab
          value="activity"
          label={`Recent Activity (${recentChanges.length})`}
          icon={<HistoryRoundedIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
        />
      </Tabs>

      {/* Rich Filter Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "#334155",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              fontSize: "12px",
            }}
          >
            <FilterAltRoundedIcon sx={{ fontSize: 16, color: "#64748b" }} /> Filter Options
          </Typography>
          {isFiltered && (
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{ textTransform: "none", fontSize: "12px", color: "error.main", py: 0 }}
            >
              Reset All Filters
            </Button>
          )}
        </Stack>

        <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
          {/* 1. Date Preset Dropdown */}
          <FormControl size="small" sx={{ minWidth: 165 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Date Preset</InputLabel>
            <Select
              value={datePreset}
              label="Date Preset"
              MenuProps={compactMenuProps}
              sx={{ fontSize: "13px" }}
              onChange={(e) => handlePresetChange(e.target.value)}
            >
              {DATE_PRESETS.map((p) => (
                <MenuItem key={p.id} value={p.id} sx={{ py: 0.5, fontSize: "12.5px" }}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 2. From Date */}
          <TextField
            type="date"
            size="small"
            label="From Date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setDatePreset("custom");
              setPage(0);
            }}
            InputLabelProps={{ shrink: true, sx: { fontSize: "13px" } }}
            inputProps={{ sx: { fontSize: "13px" } }}
            sx={{ width: 145 }}
          />

          {/* 3. To Date */}
          <TextField
            type="date"
            size="small"
            label="To Date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setDatePreset("custom");
              setPage(0);
            }}
            InputLabelProps={{ shrink: true, sx: { fontSize: "13px" } }}
            inputProps={{ sx: { fontSize: "13px" } }}
            sx={{ width: 145 }}
          />

          {/* 4. Multi-Select + Search for Events */}
          <Autocomplete
            multiple
            size="small"
            options={allAvailableEventNames}
            disableCloseOnSelect
            getOptionLabel={(option) => option}
            value={selectedEvents}
            onChange={(event, newValue) => {
              setSelectedEvents(newValue);
              setPage(0);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option} style={{ padding: "3px 8px", minHeight: 30, fontSize: "12.5px" }}>
                <Checkbox size="small" checked={selected} sx={{ p: 0.25, mr: 0.75 }} />
                <span style={{ fontSize: "12.5px" }}>{option}</span>
              </li>
            )}
            renderTags={(value, getTagProps) => {
              if (value.length === 0) return null;
              if (value.length <= 1) {
                return value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{ height: 22, fontSize: "11px", mr: 0.5 }}
                  />
                ));
              }
              return (
                <Chip
                  label={`${value.length} Events Selected`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: "11px" }}
                />
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter Events (Multi)"
                placeholder={selectedEvents.length === 0 ? "Search events..." : ""}
                size="small"
                InputLabelProps={{ sx: { fontSize: "13px" } }}
              />
            )}
            sx={{ minWidth: 240, maxWidth: 360, flexGrow: 1 }}
            ListboxProps={{ style: { maxHeight: 280, fontSize: "12.5px" } }}
          />

          {/* 5. Multi-Select + Search for Admin Users */}
          <Autocomplete
            multiple
            size="small"
            options={assignees}
            disableCloseOnSelect
            getOptionLabel={(option) => (typeof option === "string" ? option : option.name || option.email || "")}
            isOptionEqualToValue={(option, value) => {
              const optId = typeof option === "object" ? option.id : option;
              const valId = typeof value === "object" ? value.id : value;
              return optId === valId;
            }}
            value={assignees.filter((a) => selectedAdminUsers.includes(a.id))}
            onChange={(event, newValue) => {
              setSelectedAdminUsers(newValue.map((item) => (typeof item === "object" ? item.id : item)));
              setPage(0);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id} style={{ padding: "3px 8px", minHeight: 30, fontSize: "12.5px" }}>
                <Checkbox size="small" checked={selected} sx={{ p: 0.25, mr: 0.75 }} />
                <div>
                  <span style={{ fontSize: "12.5px", fontWeight: 600 }}>{option.name}</span>
                  {option.role && (
                    <span style={{ fontSize: "11px", color: "#64748b", marginLeft: 6 }}>({option.role})</span>
                  )}
                </div>
              </li>
            )}
            renderTags={(value, getTagProps) => {
              if (value.length === 0) return null;
              if (value.length <= 1) {
                return value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    sx={{ height: 22, fontSize: "11px", mr: 0.5 }}
                  />
                ));
              }
              return (
                <Chip
                  label={`${value.length} Admins Selected`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: "11px" }}
                />
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Admin User / Counselor (Multi)"
                placeholder={selectedAdminUsers.length === 0 ? "Search admin user..." : ""}
                size="small"
                InputLabelProps={{ sx: { fontSize: "13px" } }}
              />
            )}
            sx={{ minWidth: 230, maxWidth: 330, flexGrow: 1 }}
            ListboxProps={{ style: { maxHeight: 280, fontSize: "12.5px" } }}
          />

          {/* 6. Lead Source / Channel Filter */}
          <FormControl size="small" sx={{ minWidth: 165 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Lead Source</InputLabel>
            <Select
              value={sourceFilter}
              label="Lead Source"
              MenuProps={compactMenuProps}
              sx={{ fontSize: "13px" }}
              onChange={(e) => {
                setSourceFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all" sx={{ py: 0.5, fontSize: "12.5px" }}>All Sources</MenuItem>
              <MenuItem value="pdf" sx={{ py: 0.5, fontSize: "12.5px" }}>Website Brochure (PDF)</MenuItem>
              <MenuItem value="crm" sx={{ py: 0.5, fontSize: "12.5px" }}>Direct CRM Leads</MenuItem>
              <MenuItem value="callback" sx={{ py: 0.5, fontSize: "12.5px" }}>Website Callbacks</MenuItem>
              <MenuItem value="registration" sx={{ py: 0.5, fontSize: "12.5px" }}>Direct Registrations</MenuItem>
            </Select>
          </FormControl>

          {/* 7. Status Multi-Select Filter */}
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
            <InputLabel sx={{ fontSize: "13px" }}>Status (Multi)</InputLabel>
            <Select
              multiple
              value={statusFilter}
              input={<OutlinedInput label="Status (Multi)" />}
              MenuProps={compactMenuProps}
              sx={{ fontSize: "13px" }}
              renderValue={(selected) => {
                if (selected.length === 0) return "All Statuses";
                return selected
                  .map((s) => availableStatuses.find((a) => a.status === s)?.label || s)
                  .join(", ");
              }}
              onChange={(e) => {
                const val = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                setStatusFilter(val);
                setPage(0);
              }}
            >
              {availableStatuses.map((opt) => (
                <MenuItem key={opt.status} value={opt.status} sx={{ py: 0.5, fontSize: "12.5px" }}>
                  <Checkbox checked={statusFilter.indexOf(opt.status) > -1} size="small" sx={{ py: 0.25, px: 0.5 }} />
                  <ListItemText primary={opt.label || opt.status} primaryTypographyProps={{ fontSize: "12.5px" }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Selected Filter Chips Display */}
        {(selectedEvents.length > 0 || selectedAdminUsers.length > 0 || statusFilter.length > 0) && (
          <Box sx={{ mt: 1.75, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", pt: 1, borderTop: "1px dashed #f1f5f9" }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
              Active Filters:
            </Typography>

            {selectedEvents.map((ev) => (
              <Chip
                key={ev}
                label={ev}
                size="small"
                icon={<EventAvailableRoundedIcon sx={{ fontSize: "14px !important" }} />}
                onDelete={() => {
                  setSelectedEvents((prev) => prev.filter((item) => item !== ev));
                  setPage(0);
                }}
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: "11px" }}
              />
            ))}

            {selectedAdminUsers.map((uid) => {
              const u = assignees.find((a) => a.id === uid);
              return (
                <Chip
                  key={uid}
                  label={u?.name || `Admin #${uid}`}
                  size="small"
                  icon={<BadgeRoundedIcon sx={{ fontSize: "14px !important" }} />}
                  onDelete={() => {
                    setSelectedAdminUsers((prev) => prev.filter((id) => id !== uid));
                    setPage(0);
                  }}
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: "11px" }}
                />
              );
            })}

            {statusFilter.map((s) => {
              const label = availableStatuses.find((a) => a.status === s)?.label || s;
              return (
                <Chip
                  key={s}
                  label={label}
                  size="small"
                  onDelete={() => {
                    setStatusFilter((prev) => prev.filter((item) => item !== s));
                    setPage(0);
                  }}
                  variant="outlined"
                  sx={{ height: 22, fontSize: "11px", borderColor: "#cbd5e1" }}
                />
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Progress Indicator */}
      {isFetching ? <LinearProgress sx={{ mb: 2.5, borderRadius: 1 }} /> : null}

      {/* KPI Metric Summary Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Card 1: Total Leads */}
        <Tooltip title="Click to view all leads for this filter in Lead Requests" arrow>
          <Card
            onClick={() => navigateToLeadRequests()}
            sx={{
              borderRadius: 2,
              borderLeft: "4px solid #1976d2",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 20px rgba(25,118,210,0.18)" }
            }}
          >
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Total Leads & Inquiries
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, my: 0.5, color: "#1976d2" }}>
                {isLoading ? "..." : (summary.totalLeads || 0).toLocaleString("en-IN")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <GroupsRoundedIcon sx={{ fontSize: 14 }} /> Total inbound demand
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>

        {/* Card 2: Converted / Confirmed */}
        <Tooltip title="Click to view Converted leads in Lead Requests" arrow>
          <Card
            onClick={() => navigateToLeadRequests({ statuses: "converted" })}
            sx={{
              borderRadius: 2,
              borderLeft: "4px solid #2e7d32",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 20px rgba(46,125,50,0.18)" }
            }}
          >
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Converted / Confirmed
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main" }}>
                  {isLoading ? "..." : (summary.convertedCount || 0).toLocaleString("en-IN")}
                </Typography>
                <Chip
                  label={`${summary.conversionRate || 0}% Conv.`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 700, height: 22, fontSize: "11px" }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 14, color: "success.main" }} /> Confirmed & Advance Paid
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>

        {/* Card 3: Active Follow-Up */}
        <Tooltip title="Click to view Active Pipeline leads in Lead Requests" arrow>
          <Card
            onClick={() => navigateToLeadRequests({ statuses: "follow_up_done,second_follow_up_done,third_follow_up_done,call_back_required" })}
            sx={{
              borderRadius: 2,
              borderLeft: "4px solid #ed6c02",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 20px rgba(237,108,2,0.18)" }
            }}
          >
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Active Pipeline
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#ed6c02" }}>
                  {isLoading ? "..." : (summary.inFollowUpCount || 0).toLocaleString("en-IN")}
                </Typography>
                <Chip
                  label={`${summary.inFollowUpRate || 0}%`}
                  size="small"
                  sx={{ fontWeight: 700, height: 22, fontSize: "11px", bgcolor: "#fff7ed", color: "#c2410c" }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <PhoneInTalkRoundedIcon sx={{ fontSize: 14 }} /> In follow-up / callback
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>

        {/* Card 4: New / Uncontacted */}
        <Tooltip title="Click to view New / Not Set leads in Lead Requests" arrow>
          <Card
            onClick={() => navigateToLeadRequests({ statuses: "(not set)" })}
            sx={{
              borderRadius: 2,
              borderLeft: "4px solid #7c3aed",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 20px rgba(124,58,237,0.18)" }
            }}
          >
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                New / Not Set
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#7c3aed" }}>
                  {isLoading ? "..." : (summary.uncontactedCount || 0).toLocaleString("en-IN")}
                </Typography>
                <Chip
                  label={`${summary.uncontactedRate || 0}%`}
                  size="small"
                  sx={{ fontWeight: 700, height: 22, fontSize: "11px", bgcolor: "#f3e8ff", color: "#6b21a8" }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <HourglassEmptyRoundedIcon sx={{ fontSize: 14 }} /> Awaiting first contact
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>

        {/* Card 5: Lost / Dropped */}
        <Tooltip title="Click to view Lost / Dropped leads in Lead Requests" arrow>
          <Card
            onClick={() => navigateToLeadRequests({ statuses: "not_interested,no_response" })}
            sx={{
              borderRadius: 2,
              borderLeft: "4px solid #d32f2f",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 20px rgba(211,47,47,0.18)" }
            }}
          >
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Lost / Dropped
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ my: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#d32f2f" }}>
                  {isLoading ? "..." : (summary.lostCount || 0).toLocaleString("en-IN")}
                </Typography>
                <Chip
                  label={`${summary.lostRate || 0}%`}
                  size="small"
                  sx={{ fontWeight: 700, height: 22, fontSize: "11px", bgcolor: "#fee2e2", color: "#b91c1c" }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CancelRoundedIcon sx={{ fontSize: 14 }} /> No response / not interested
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>
      </Box>

      {/* Visual Graphs & Pipeline (Displayed on Overview tab) */}
      {activeTab === "overview" && (
        <>
          {/* Top Row: Re-architected Trend Chart + Clean Lead Channels */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
              gap: 2.5,
              mb: 3,
            }}
          >
            {/* Visual Timeline Trend Chart with Y-Axis & Gridlines */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1, color: "#1e293b" }}>
                    <TrendingUpRoundedIcon color="primary" /> Lead Volume & Conversion Trend
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inbound leads vs confirmed conversions over time
                  </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: "#93c5fd", borderRadius: 0.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>Total Leads</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: "#10b981", borderRadius: 0.5 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>Converted</Typography>
                  </Box>

                  {/* Day vs Week View Toggle */}
                  <ToggleButtonGroup
                    value={chartViewMode}
                    exclusive
                    size="small"
                    onChange={(e, val) => val && setChartViewMode(val)}
                    sx={{ height: 28 }}
                  >
                    <ToggleButton value="day" sx={{ px: 1, py: 0, fontSize: "11px", textTransform: "none", fontWeight: 600 }}>
                      Daily
                    </ToggleButton>
                    <ToggleButton value="week" sx={{ px: 1, py: 0, fontSize: "11px", textTransform: "none", fontWeight: 600 }}>
                      Weekly
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Stack>

              {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : processedChartData.length === 0 ? (
                <EmptyState title="No trend data" description="No leads found for this date range." />
              ) : (
                <Box sx={{ position: "relative", height: 220, width: "100%", pt: 1, pb: 4 }}>
                  {/* Horizontal Gridlines & Y-Axis Scale */}
                  {[1, 0.75, 0.5, 0.25, 0].map((ratio) => {
                    const yVal = Math.round(maxChartVal * ratio);
                    return (
                      <Box
                        key={ratio}
                        sx={{
                          position: "absolute",
                          top: `${(1 - ratio) * 160 + 12}px`,
                          left: 0,
                          right: 0,
                          display: "flex",
                          alignItems: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ width: 36, fontSize: "10px", color: "#94a3b8", textAlign: "right", pr: 1 }}
                        >
                          {yVal}
                        </Typography>
                        <Box sx={{ flexGrow: 1, borderTop: "1px dashed #e2e8f0" }} />
                      </Box>
                    );
                  })}

                  {/* Scrollable / Centered Bars Area */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 40,
                      right: 8,
                      top: 12,
                      height: 160,
                      overflowX: "auto",
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: processedChartData.length < 15 ? "center" : "flex-start",
                      gap: processedChartData.length < 15 ? 2.5 : 1,
                    }}
                  >
                    {processedChartData.map((d, idx) => {
                      const totalVal = Number(d.leads || 0);
                      const convVal = Number(d.converted || 0);
                      const heightPct = Math.max(3, Math.round((totalVal / maxChartVal) * 100));
                      const convPct = totalVal > 0 ? Math.round((convVal / totalVal) * 100) : 0;

                      return (
                        <Tooltip
                          key={d.date || idx}
                          arrow
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, display: "block", color: "#f8fafc" }}>
                                {d.label || d.date}
                              </Typography>
                              <Typography variant="caption" sx={{ display: "block", color: "#93c5fd" }}>
                                Total Leads: {totalVal.toLocaleString("en-IN")}
                              </Typography>
                              <Typography variant="caption" sx={{ display: "block", color: "#4ade80" }}>
                                Converted: {convVal.toLocaleString("en-IN")} ({convPct}%)
                              </Typography>
                            </Box>
                          }
                        >
                          <Box
                            sx={{
                              flex: processedChartData.length < 15 ? "0 0 auto" : "1 1 0",
                              maxWidth: 34,
                              minWidth: 14,
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              cursor: "pointer",
                            }}
                          >
                            {/* Bar Column */}
                            <Box
                              sx={{
                                width: "100%",
                                height: `${heightPct}%`,
                                bgcolor: "#dbeafe",
                                borderRadius: "4px 4px 0 0",
                                position: "relative",
                                overflow: "hidden",
                                transition: "all 0.15s ease-in-out",
                                "&:hover": { bgcolor: "#bfdbfe", transform: "scaleY(1.02)" },
                              }}
                            >
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  height: `${convPct}%`,
                                  bgcolor: "#10b981",
                                  borderRadius: convPct >= 95 ? "4px 4px 0 0" : 0,
                                }}
                              />
                            </Box>

                            {/* Date Label Underneath */}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "10px",
                                color: "#64748b",
                                mt: 0.75,
                                whiteSpace: "nowrap",
                                fontWeight: 500,
                              }}
                            >
                              {d.date ? d.date.replace(/^\d{4}-/, "") : ""}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Lead Channels Breakdown with Distinct Categories */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                <PersonAddRoundedIcon color="primary" /> Lead Channels
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Breakdown of inbound leads by acquisition channel
              </Typography>

              <Stack spacing={2}>
                {byCategory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No category data found.</Typography>
                ) : (
                  byCategory.map((cat) => {
                    const totalAll = byCategory.reduce((acc, c) => acc + (c.count || 0), 0);
                    const pct = totalAll > 0 ? Math.round((cat.count / totalAll) * 100) : 0;

                    let icon = <GroupsRoundedIcon sx={{ fontSize: 18, color: "#2563eb" }} />;
                    let barColor = "#2563eb";

                    if (cat.iconType === "pdf") {
                      icon = <PictureAsPdfRoundedIcon sx={{ fontSize: 18, color: "#dc2626" }} />;
                      barColor = "#dc2626";
                    } else if (cat.iconType === "incoming_call") {
                      icon = <PhoneInTalkRoundedIcon sx={{ fontSize: 18, color: "#4f46e5" }} />;
                      barColor = "#4f46e5";
                    } else if (cat.iconType === "office_visit") {
                      icon = <StoreRoundedIcon sx={{ fontSize: 18, color: "#9333ea" }} />;
                      barColor = "#9333ea";
                    } else if (cat.iconType === "callback") {
                      icon = <PhoneCallbackRoundedIcon sx={{ fontSize: 18, color: "#d97706" }} />;
                      barColor = "#d97706";
                    } else if (cat.iconType === "registration") {
                      icon = <HowToRegRoundedIcon sx={{ fontSize: 18, color: "#059669" }} />;
                      barColor = "#059669";
                    }

                    return (
                      <Box
                        key={cat.label}
                        onClick={() => navigateToLeadRequests({ source: cat.key || cat.source || cat.iconType })}
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                          "&:hover": { bgcolor: "#f1f5f9" }
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {icon}
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
                              {cat.label}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a" }}>
                            {cat.count.toLocaleString("en-IN")}{" "}
                            <span style={{ color: "#64748b", fontWeight: 400 }}>({pct}%)</span>
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: "#f1f5f9",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: barColor,
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                    );
                  })
                )}
              </Stack>
            </Paper>
          </Box>

          {/* Event-wise Data Table Card */}
          <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <Box
              sx={{
                p: 2,
                bgcolor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                  Event Performance Breakdown
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
                  Showing {filteredEvents.length} events
                </Typography>
              </Box>

              {/* Table search filter */}
              <TextField
                size="small"
                placeholder="Search event in table..."
                value={searchTableEvent}
                onChange={(e) => {
                  setSearchTableEvent(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: "13px", bgcolor: "#ffffff", width: 240 },
                }}
              />
            </Box>

            {isLoading ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={32} />
              </Box>
            ) : filteredEvents.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <EmptyState
                  title="No event records found"
                  description="Try adjusting your date range, source, or search filters."
                />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                      <TableRow>
                        <TableCell
                          onClick={() => handleSort("event")}
                          sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                        >
                          Event Name {sortField === "event" && (sortOrder === "asc" ? "↑" : "↓")}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort("total")}
                          sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                        >
                          Total Leads {sortField === "total" && (sortOrder === "asc" ? "↑" : "↓")}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort("converted")}
                          sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                        >
                          Converted / Confirmed (Paid) {sortField === "converted" && (sortOrder === "asc" ? "↑" : "↓")}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort("inFollowUp")}
                          sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                        >
                          Active Pipeline {sortField === "inFollowUp" && (sortOrder === "asc" ? "↑" : "↓")}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort("lost")}
                          sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                        >
                          Lost / Dropped {sortField === "lost" && (sortOrder === "asc" ? "↑" : "↓")}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={() => handleSort("conversionRate")}
                          sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                        >
                          Conversion Rate {sortField === "conversionRate" && (sortOrder === "asc" ? "↑" : "↓")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedEvents.map((row, idx) => {
                        const total = Number(row.total || 0);
                        const converted = Number(row.converted || 0);
                        const inFollowUp = Number(row.inFollowUp || 0);
                        const lost = Number(row.lost || 0);
                        const rate = row.conversionRate || (total > 0 ? Math.round((converted / total) * 100) : 0);

                        return (
                          <TableRow
                            key={row.event + idx}
                            hover
                            onClick={() => navigateToLeadRequests({ events: row.event })}
                            sx={{
                              cursor: "pointer",
                              "&:hover": { bgcolor: "#f1f5f9" },
                              transition: "background 0.15s"
                            }}
                          >
                            <TableCell sx={{ fontWeight: 600, color: "#1976d2", fontSize: "13px", "&:hover": { textDecoration: "underline" } }}>
                              {row.event}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: "13px" }}>
                              {total.toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToLeadRequests({ events: row.event, statuses: "converted" });
                              }}
                            >
                              <Tooltip title={`View Converted leads for ${row.event}`} arrow>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: "success.main", fontSize: "13px", "&:hover": { textDecoration: "underline" } }}>
                                  {converted.toLocaleString("en-IN")}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToLeadRequests({ events: row.event, statuses: "follow_up_done,second_follow_up_done,third_follow_up_done,call_back_required" });
                              }}
                            >
                              <Tooltip title={`View Active Pipeline for ${row.event}`} arrow>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: inFollowUp > 0 ? "#ed6c02" : "#94a3b8", fontSize: "13px", "&:hover": { textDecoration: "underline" } }}>
                                  {inFollowUp.toLocaleString("en-IN")}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell
                              align="right"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigateToLeadRequests({ events: row.event, statuses: "not_interested,no_response" });
                              }}
                            >
                              <Tooltip title={`View Lost leads for ${row.event}`} arrow>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: lost > 0 ? "#dc2626" : "#94a3b8", fontSize: "13px", "&:hover": { textDecoration: "underline" } }}>
                                  {lost.toLocaleString("en-IN")}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(rate, 100)}
                                  sx={{
                                    width: 50,
                                    height: 5,
                                    borderRadius: 2.5,
                                    bgcolor: "#f1f5f9",
                                    "& .MuiLinearProgress-bar": {
                                      bgcolor: rate > 30 ? "#16a34a" : rate > 10 ? "#eab308" : "#94a3b8",
                                      borderRadius: 2.5,
                                    },
                                  }}
                                />
                                <span
                                  style={{
                                    background: rate > 30 ? "#dcfce7" : rate > 10 ? "#fef9c3" : "#f1f5f9",
                                    color: rate > 30 ? "#166534" : rate > 10 ? "#854d0e" : "#475569",
                                    padding: "3px 8px",
                                    borderRadius: "12px",
                                    fontSize: "0.78rem",
                                    fontWeight: 700,
                                    minWidth: 38,
                                    textAlign: "center",
                                    display: "inline-block",
                                  }}
                                >
                                  {rate}%
                                </span>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredEvents.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 15, 25, 50, 100]}
                  sx={{ borderTop: "1px solid #e2e8f0" }}
                />
              </>
            )}
          </Paper>
        </>
      )}

      {/* Users / Counselors Performance Tab */}
      {activeTab === "user_performance" && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
                Counselor & Team Performance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Breakdown of leads handled, conversions, active follow-ups, and pipeline distribution by counselor / user.
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Search user, email or role..."
              value={searchTableUser}
              onChange={(e) => {
                setSearchTableUser(e.target.value);
                setUserPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                sx: { fontSize: "13px", bgcolor: "#ffffff", width: { xs: "100%", sm: 260 } },
              }}
            />
          </Box>

          {isLoading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={32} />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <EmptyState
                title="No user records found"
                description="Try adjusting your date range, source, or search filters."
              />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 700 }}>
                  <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                    <TableRow>
                      <TableCell
                        onClick={() => handleUserSort("name")}
                        sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                      >
                        User / Counselor {userSortField === "name" && (userSortOrder === "asc" ? "↑" : "↓")}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={() => handleUserSort("total")}
                        sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                      >
                        Total Leads {userSortField === "total" && (userSortOrder === "asc" ? "↑" : "↓")}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={() => handleUserSort("converted")}
                        sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                      >
                        Converted {userSortField === "converted" && (userSortOrder === "asc" ? "↑" : "↓")}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={() => handleUserSort("active")}
                        sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                      >
                        Active Pipeline {userSortField === "active" && (userSortOrder === "asc" ? "↑" : "↓")}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={() => handleUserSort("newOrNotSet")}
                        sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                      >
                        New / Not Set {userSortField === "newOrNotSet" && (userSortOrder === "asc" ? "↑" : "↓")}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={() => handleUserSort("lost")}
                        sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                      >
                        Lost / Dropped {userSortField === "lost" && (userSortOrder === "asc" ? "↑" : "↓")}
                      </TableCell>
                      <TableCell
                        align="right"
                        onClick={() => handleUserSort("conversionRate")}
                        sx={{ fontWeight: 700, color: "#475569", cursor: "pointer", fontSize: "12.5px" }}
                      >
                        Conversion Rate {userSortField === "conversionRate" && (userSortOrder === "asc" ? "↑" : "↓")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((u, idx) => {
                      const total = Number(u.total || 0);
                      const converted = Number(u.converted || 0);
                      const active = Number(u.active || 0);
                      const newOrNotSet = Number(u.newOrNotSet || 0);
                      const lost = Number(u.lost || 0);
                      const rate = u.conversionRate ?? (total > 0 ? Math.round((converted / total) * 1000) / 10 : 0);

                      const initials = (u.name || "U")
                        .split(" ")
                        .map((n) => n[0])
                        .filter(Boolean)
                        .slice(0, 2)
                        .join("")
                        .toUpperCase();

                      return (
                        <TableRow
                          key={(u.userId || idx) + "-row"}
                          hover
                          onClick={() => navigateToLeadRequests(u.userId ? { adminUsers: String(u.userId) } : { statuses: "(not set)" })}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#f1f5f9" },
                            transition: "background 0.15s"
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "50%",
                                  bgcolor: u.userId ? "#e0f2fe" : "#fef3c7",
                                  color: u.userId ? "#0369a1" : "#b45309",
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {u.userId ? initials : "UN"}
                              </Box>
                              <Box>
                                <Typography sx={{ fontWeight: 600, color: u.userId ? "#1976d2" : "#b45309", fontSize: "13px", "&:hover": { textDecoration: "underline" } }}>
                                  {u.name}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                                  <Chip
                                    label={u.userId ? (u.role || "staff") : "Awaiting Assignment"}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: "10.5px",
                                      fontWeight: 600,
                                      bgcolor: u.userId ? "#f1f5f9" : "#fff7ed",
                                      color: u.userId ? "#475569" : "#c2410c",
                                      textTransform: "capitalize",
                                    }}
                                  />
                                  <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11px" }}>
                                    {u.email || (u.userId ? "" : "Inbound queue awaiting counselor contact")}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: "13.5px", color: "#0f172a" }}>
                            {total.toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToLeadRequests(u.userId ? { adminUsers: String(u.userId), statuses: "converted" } : { statuses: "converted" });
                            }}
                          >
                            <Tooltip title={`View Converted leads for ${u.name}`} arrow>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "success.main", fontSize: "13px", "&:hover": { textDecoration: "underline" } }}>
                                {converted.toLocaleString("en-IN")}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToLeadRequests(u.userId ? { adminUsers: String(u.userId), statuses: "follow_up_done,second_follow_up_done,third_follow_up_done,call_back_required" } : { statuses: "follow_up_done,second_follow_up_done,third_follow_up_done,call_back_required" });
                            }}
                          >
                            <Tooltip title={`View Active Pipeline for ${u.name}`} arrow>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: active > 0 ? "#ed6c02" : "#94a3b8", fontSize: "13px", "&:hover": { textDecoration: "underline" } }}>
                                {active.toLocaleString("en-IN")}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToLeadRequests(u.userId ? { adminUsers: String(u.userId), statuses: "(not set)" } : { statuses: "(not set)" });
                            }}
                          >
                            <Tooltip title={`View New / Not Set leads for ${u.name}`} arrow>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: newOrNotSet > 0 ? "#7c3aed" : "#94a3b8", fontSize: "13px", "&:hover": { textDecoration: "underline" } }}>
                                {newOrNotSet.toLocaleString("en-IN")}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigateToLeadRequests(u.userId ? { adminUsers: String(u.userId), statuses: "not_interested,no_response" } : { statuses: "not_interested,no_response" });
                            }}
                          >
                            <Tooltip title={`View Lost leads for ${u.name}`} arrow>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: lost > 0 ? "#dc2626" : "#94a3b8", fontSize: "13px", "&:hover": { textDecoration: "underline" } }}>
                                {lost.toLocaleString("en-IN")}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(rate, 100)}
                                sx={{
                                  width: 50,
                                  height: 5,
                                  borderRadius: 2.5,
                                  bgcolor: "#f1f5f9",
                                  "& .MuiLinearProgress-bar": {
                                    bgcolor: rate > 30 ? "#16a34a" : rate > 10 ? "#eab308" : "#94a3b8",
                                    borderRadius: 2.5,
                                  },
                                }}
                              />
                              <span
                                style={{
                                  background: rate > 30 ? "#dcfce7" : rate > 10 ? "#fef9c3" : "#f1f5f9",
                                  color: rate > 30 ? "#166534" : rate > 10 ? "#854d0e" : "#475569",
                                  padding: "3px 8px",
                                  borderRadius: "12px",
                                  fontSize: "0.78rem",
                                  fontWeight: 700,
                                  minWidth: 42,
                                  textAlign: "center",
                                  display: "inline-block",
                                }}
                              >
                                {rate}%
                              </span>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filteredUsers.length}
                page={userPage}
                onPageChange={(e, newP) => setUserPage(newP)}
                rowsPerPage={userRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setUserRowsPerPage(parseInt(e.target.value, 10));
                  setUserPage(0);
                }}
                rowsPerPageOptions={[10, 15, 25, 50, 100]}
                sx={{ borderTop: "1px solid #e2e8f0" }}
              />
            </>
          )}
        </Paper>
      )}

      {/* Status Pipeline Breakdown Tab */}
      {activeTab === "status_breakdown" && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px solid #e2e8f0" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
            Lead Status Distribution
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Understand the volume and conversion funnel progression across all status stages.
          </Typography>

          <Stack spacing={2.5}>
            {byStatus.map((item) => {
              const count = Number(item.count || 0);
              const total = summary.totalLeads || 1;
              const pct = item.percentage !== undefined ? item.percentage : Math.round((count / total) * 1000) / 10;
              const styling = getStatusColor(item.status);

              return (
                <Tooltip key={item.status} title={`Click to view ${item.label || item.status} leads in Lead Requests`} arrow>
                  <Box
                    onClick={() => navigateToLeadRequests({ statuses: item.status })}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${styling.border}40`,
                      bgcolor: styling.bg,
                      cursor: "pointer",
                      transition: "all 0.15s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        borderColor: styling.border
                      }
                    }}
                  >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Chip
                        label={item.label || item.status}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          bgcolor: styling.color,
                          color: "#ffffff",
                          fontSize: "12px",
                        }}
                      />
                      <Typography variant="caption" sx={{ color: "#475569", fontWeight: 600 }}>
                        {item.status}
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: styling.color }}>
                      {count.toLocaleString("en-IN")}{" "}
                      <span style={{ fontSize: "13px", fontWeight: 500, color: "#64748b" }}>
                        ({pct}%)
                      </span>
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(pct, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "rgba(0,0,0,0.06)",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: styling.color,
                        borderRadius: 4,
                      },
                    }}
                  />
                  </Box>
                </Tooltip>
              );
            })}
          </Stack>
        </Paper>
      )}

      {/* Recent Activity / Status Changes Tab */}
      {activeTab === "activity" && (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: "1px solid #e2e8f0" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
            Recent Lead Status Movements
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Audit trail of lead status transitions logged by the counseling team.
          </Typography>

          {recentChanges.length === 0 ? (
            <EmptyState title="No status logs" description="No status transitions recorded for this period." />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Lead Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>From Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>To Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Changed By</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Date & Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentChanges.slice(0, 50).map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{log.leadType}</TableCell>
                      <TableCell>
                        <Chip
                          label={log.fromLabel || log.fromStatus || "Not Set"}
                          size="small"
                          sx={{ height: 22, fontSize: "11px" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.toLabel || log.toStatus}
                          size="small"
                          color="primary"
                          sx={{ height: 22, fontSize: "11px", fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#334155" }}>{log.changedByName}</TableCell>
                      <TableCell align="right" sx={{ color: "#64748b", fontSize: "12px" }}>
                        {log.createdAt ? new Date(log.createdAt).toLocaleString("en-IN") : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </div>
  );
};

export default LeadReports;
